import { useQuery, UseQueryResult } from "@tanstack/react-query";

import { ISoccerMatchListItem, ISoccerMatches } from "../../../types";
import {
  getMatch,
  getMatches,
  pickDefaultMatchFromList,
} from "../api";

// Shares the ["schedule-matches", tournamentId] cache key with useScheduleMatches
// so the carousel + default-match resolution use ONE network call instead of
// 1 + 3 fan-out. The default match is derived in-memory via select().
export const useDefaultMatch = (
  tournamentId: string,
  matchId: string | null
): Pick<
  UseQueryResult<ISoccerMatchListItem | undefined>,
  "data" | "isLoading" | "error" | "refetch"
> => {
  const hasMatchId = Boolean(matchId);

  const fixtureQuery = useQuery({
    queryKey: [`fixture-${matchId}`],
    queryFn: () => getMatch({ matchId: matchId || "", liveScore: true }),
    enabled: hasMatchId,
    refetchOnWindowFocus: false,
    staleTime: 3 * 60 * 60 * 1000,
  });

  const scheduleQuery = useQuery<ISoccerMatches, Error, ISoccerMatchListItem | undefined>({
    queryKey: ["schedule-matches", tournamentId],
    queryFn: () =>
      getMatches({
        tournamentId,
        size: 500,
        sort: "asc",
        liveScore: true,
      }),
    enabled: !hasMatchId,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
    select: (data) =>
      pickDefaultMatchFromList(data?.matches ?? [], tournamentId) ?? undefined,
  });

  const active = hasMatchId ? fixtureQuery : scheduleQuery;
  return {
    isLoading: active.isLoading,
    error: active.error,
    data: active.data ?? undefined,
    refetch: active.refetch,
  };
};
